export const pharmacology2Module = {
  id: "pharmacology2",
  title: "Renal Pharmacology: Part 2",
  sections: [
    {
      type: "text",
      title: "Renal Transplant Immunosuppression: Overview & Induction",
      content: `
        <p>Transplant immunosuppression follows a structured protocol designed to prevent rejection while minimising toxicity. It is divided into <span class="purple-term">induction</span> (at transplant) and <span class="purple-term">maintenance</span> (long-term).</p>

        <div class="highlight-box">
          <p><strong>Phases of immunosuppression</strong></p>
          <ul>
            <li><strong>Induction</strong>: high-intensity immunosuppression at the time of transplant to prevent early acute rejection; given for days to weeks</li>
            <li><strong>Maintenance</strong>: lower-intensity long-term regimen to prevent chronic rejection; lifelong in most patients</li>
            <li><strong>Rescue/anti-rejection</strong>: high-dose treatment given when acute rejection is diagnosed</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Induction agents</strong></p>
          <ul>
            <li><span class="purple-term">Basiliximab</span> — anti-IL-2 receptor (anti-CD25) monoclonal antibody; blocks IL-2 signalling required for T-cell proliferation; given on Day 0 and Day 4; well tolerated; used in standard-risk recipients</li>
            <li><span class="purple-term">Anti-thymocyte globulin (ATG/thymoglobulin)</span> — polyclonal rabbit antibody against T-cell antigens; depletes T cells by complement-mediated lysis and ADCC; used in high-risk situations:
              <ul>
                <li>High immunological risk (high panel reactive antibody, repeat transplant)</li>
                <li>Delayed graft function — allows CNI deferral while graft recovers</li>
                <li>Conversion therapy (borderline/rejection)</li>
              </ul>
            </li>
            <li>Side effects of ATG: cytokine release syndrome (fever, rigors, hypotension — first dose reaction); profound lymphopenia → increased infection risk; serum sickness</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Standard maintenance triple therapy</strong></p>
          <ul>
            <li>Most centres use: <span class="purple-term">Tacrolimus + Mycophenolate mofetil (MMF) + Prednisolone</span></li>
            <li>Each drug targets a different step of T-cell activation:
              <ul>
                <li>Tacrolimus → blocks calcineurin → no IL-2 transcription</li>
                <li>MMF → blocks IMPDH → no purine synthesis → no T/B-cell proliferation</li>
                <li>Prednisolone → broad anti-inflammatory, reduces cytokine production</li>
              </ul>
            </li>
            <li>Some centres wean off prednisolone at 3–6 months (steroid-free maintenance) in low-risk patients</li>
          </ul>
        </div>
      `,
    },
    {
      type: "text",
      title: "Calcineurin Inhibitors: Tacrolimus & Ciclosporin",
      content: `
        <p>Calcineurin inhibitors (CNIs) are the cornerstone of transplant maintenance immunosuppression. They prevent T-cell activation by blocking calcineurin, the phosphatase that activates NFAT (nuclear factor of activated T cells).</p>

        <div class="highlight-box">
          <p><strong>Tacrolimus (FK506)</strong></p>
          <ul>
            <li>Mechanism: binds <strong>FK-binding protein (FKBP-12)</strong> → complex inhibits calcineurin → prevents NFAT dephosphorylation → no IL-2 gene transcription → no T-cell proliferation</li>
            <li><strong>Monitoring</strong>: narrow therapeutic index; measure <span class="purple-term">trough (pre-dose) blood levels</span>; target:
              <ul>
                <li>Early post-transplant (0–3 months): 8–12 ng/mL</li>
                <li>Maintenance (&gt;12 months): 4–8 ng/mL</li>
              </ul>
            </li>
            <li><strong>Metabolism</strong>: CYP3A4 and P-glycoprotein substrate — extensive drug interactions; inhibitors (azole antifungals, diltiazem, grapefruit juice, erythromycin) ↑ tacrolimus levels; inducers (rifampicin, phenytoin, St. John's Wort) ↓ tacrolimus levels → risk of rejection</li>
            <li><strong>Toxicities:</strong>
              <ul>
                <li><span class="purple-term">Nephrotoxicity</span>: afferent arteriole vasoconstriction → reduced GFR; dose-dependent and reversible; chronic CNI nephrotoxicity causes interstitial fibrosis; distinguish from rejection by biopsy</li>
                <li><span class="purple-term">Neurotoxicity</span>: tremor (most common), headache, posterior reversible encephalopathy syndrome (PRES) at high levels</li>
                <li><span class="purple-term">New-onset diabetes after transplantation (NODAT)</span>: tacrolimus impairs insulin secretion from beta cells (>ciclosporin); manage with insulin or oral hypoglycaemics</li>
                <li>Hypertension, hyperkalaemia (reduces K⁺ excretion), hypomagnesaemia</li>
              </ul>
            </li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Ciclosporin (cyclosporine A)</strong></p>
          <ul>
            <li>Mechanism: binds <strong>cyclophilin</strong> → complex inhibits calcineurin → same downstream pathway as tacrolimus</li>
            <li>Now used less than tacrolimus (tacrolimus associated with lower rejection rates in most studies)</li>
            <li><strong>Unique toxicities</strong> compared to tacrolimus:
              <ul>
                <li><span class="purple-term">Gingival hyperplasia</span> — proliferative overgrowth of gum tissue; dose-dependent; improved with good dental hygiene and dose reduction</li>
                <li><span class="purple-term">Hypertrichosis</span> — excessive hair growth, particularly on the face</li>
                <li><span class="purple-term">Dyslipidaemia</span> — significant hypercholesterolaemia (more than tacrolimus)</li>
                <li>Nephrotoxicity and hypertension: similar to tacrolimus</li>
                <li>Less diabetogenic than tacrolimus</li>
              </ul>
            </li>
            <li>Monitoring: trough levels or C2 (2-hour post-dose) levels; same CYP3A4 drug interaction profile</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Ciclosporin's Unique Side Effects — "GHD"</div>
          <div class="mnemonic-content"><strong>G</strong>ingival hyperplasia<br/><strong>H</strong>ypertrichosis<br/><strong>D</strong>yslipidaemia (hypercholesterolaemia)<br/>(Both CNIs cause nephrotoxicity, neurotoxicity, hypertension, hyperkalaemia)</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A renal transplant recipient on tacrolimus develops tremor, a creatinine that has risen from 120 to 180 µmol/L, and a tacrolimus trough level of 18 ng/mL (target 8–12 ng/mL). They were recently started on fluconazole for oral candidiasis. What is the most likely explanation?",
      options: [
        "Acute T-cell rejection — start pulse IV methylprednisolone immediately",
        "Fluconazole inhibits CYP3A4 → elevated tacrolimus levels → tacrolimus nephrotoxicity; reduce tacrolimus dose and recheck levels",
        "Ciclosporin-induced gingival hyperplasia — switch to tacrolimus",
        "MMF-induced nephrotoxicity — stop MMF and monitor creatinine",
      ],
      correct: 1,
      explanation:
        "Tacrolimus is metabolised by CYP3A4. Fluconazole is a potent CYP3A4 inhibitor — co-prescription without tacrolimus dose reduction almost invariably leads to toxic drug accumulation. The patient has the classic features: elevated trough level (18 ng/mL vs target 8–12), neurotoxicity (tremor), and worsening renal function (CNI nephrotoxicity via afferent arteriole vasoconstriction). Management: reduce tacrolimus dose, repeat levels in 24–48 hours, and maintain the level within the therapeutic range. Rejection is less likely with an elevated drug level. Common CYP3A4 inhibitors to know: azole antifungals (fluconazole, voriconazole, ketoconazole), diltiazem, grapefruit juice, erythromycin.",
    },
    {
      type: "text",
      title: "Antiproliferative Agents: MMF & Azathioprine",
      content: `
        <p>Antiproliferative agents prevent lymphocyte clonal expansion after alloantigen recognition by blocking nucleotide synthesis. They are given alongside a CNI and prednisolone in triple therapy.</p>

        <div class="highlight-box">
          <p><strong>Mycophenolate mofetil (MMF) / mycophenolate sodium (EC-MPS)</strong></p>
          <ul>
            <li>MMF is a prodrug → hydrolysed to <strong>mycophenolic acid (MPA)</strong> — the active drug</li>
            <li>Mechanism: inhibits <span class="glossary-term">inosine monophosphate dehydrogenase (IMPDH)</span> — the rate-limiting enzyme in the <em>de novo</em> purine synthesis pathway; lymphocytes (unlike most cells) rely heavily on de novo purine synthesis and cannot salvage purines efficiently → selective lymphocyte suppression</li>
            <li>Advantages over azathioprine: more selective; lower risk of malignancy; higher efficacy in preventing acute rejection</li>
            <li><strong>Side effects:</strong>
              <ul>
                <li><span class="purple-term">GI toxicity</span>: nausea, diarrhoea, abdominal cramps — most common dose-limiting toxicity; switch to EC-MPS (enteric-coated) formulation to reduce GI effects</li>
                <li>Leucopenia and thrombocytopenia — monitor FBC</li>
                <li><span class="purple-term">Teratogenicity</span>: category D; causes miscarriage and fetal malformations (cleft palate, digital anomalies); must be <strong>stopped ≥6 weeks before conception</strong> and is absolutely contraindicated in pregnancy; switch to azathioprine for pregnancy</li>
                <li>Increased infection risk (CMV, PCP — prophylaxis important)</li>
              </ul>
            </li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Azathioprine</strong></p>
          <ul>
            <li>Prodrug → converted to <strong>6-mercaptopurine (6-MP)</strong> → incorporated into DNA as a fraudulent purine → inhibits de novo purine synthesis + causes DNA strand breaks → blocks T- and B-cell proliferation</li>
            <li>Metabolised by <span class="purple-term">thiopurine methyltransferase (TPMT)</span>; the active metabolite 6-thioguanine nucleotide (6-TGN) causes myelosuppression</li>
            <li><strong>Critical drug interaction</strong>: <span class="purple-term">allopurinol</span> inhibits xanthine oxidase, which metabolises 6-MP to inactive metabolites; if allopurinol is given with azathioprine, 6-MP accumulates → severe myelosuppression (potentially fatal); reduce azathioprine dose by <strong>75%</strong> if allopurinol is unavoidable, or switch to MMF</li>
            <li><strong>TPMT genotyping</strong>: patients with low or absent TPMT activity have massive 6-TGN accumulation → life-threatening myelosuppression at standard doses; test TPMT activity or genotype before starting azathioprine; homozygous deficiency → use alternative drug</li>
            <li>Used over MMF in pregnancy (safer fetal profile than MMF; still associated with some risk)</li>
            <li>Side effects: myelosuppression, hepatotoxicity (cholestatic jaundice), increased malignancy risk (particularly SCC and lymphoma)</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A renal transplant patient on azathioprine 100 mg/day develops gout and is started on allopurinol by their rheumatologist without consulting nephrology. Three weeks later, they are admitted with a white cell count of 0.5 × 10⁹/L and neutrophil count of 0.2 × 10⁹/L. What has happened?",
      options: [
        "Allopurinol directly suppresses bone marrow — switch azathioprine to MMF",
        "Allopurinol inhibits xanthine oxidase, preventing 6-mercaptopurine breakdown → toxic accumulation of 6-TGN → severe myelosuppression; azathioprine must be dose-reduced by 75% or stopped and switched to MMF",
        "CMV bone marrow suppression triggered by allopurinol-induced CMV reactivation",
        "Azathioprine interacts with allopurinol at the renal tubule, causing azathioprine overdose",
      ],
      correct: 1,
      explanation:
        "This is the classic and dangerous azathioprine–allopurinol interaction. Azathioprine is converted to 6-mercaptopurine (6-MP), which is normally metabolised to inactive metabolites by xanthine oxidase. Allopurinol inhibits xanthine oxidase → 6-MP cannot be inactivated → shunted to 6-thioguanine nucleotides (6-TGN) → severe myelosuppression. This interaction is well-documented and potentially fatal. If allopurinol is necessary, azathioprine dose must be reduced by 75% — but in practice, switching to MMF (which has no such interaction) is the safest approach. All prescribers must be alerted to this interaction when starting allopurinol in transplant patients.",
    },
    {
      type: "text",
      title: "mTOR Inhibitors & Corticosteroids in Transplantation",
      content: `
        <p>mTOR inhibitors and corticosteroids complete the immunosuppressive armamentarium in transplantation, and have important roles beyond immune suppression.</p>

        <div class="highlight-box">
          <p><strong>mTOR inhibitors: sirolimus and everolimus</strong></p>
          <ul>
            <li>Mechanism: bind FKBP-12 (same binding protein as tacrolimus) → complex inhibits <span class="glossary-term">mTOR (mechanistic target of rapamycin)</span> → blocks cell cycle progression at G1→S transition → inhibits T-cell and B-cell proliferation driven by growth factor signals (distinct from IL-2 pathway)</li>
            <li>Advantages: not nephrotoxic (unlike CNIs); antiproliferative effect against some cancers (used in RCC therapy)</li>
            <li><strong>Key indications in transplantation:</strong>
              <ul>
                <li>CNI minimisation/withdrawal protocols — reduce CNI-related nephrotoxicity</li>
                <li>Post-transplant malignancy (particularly skin cancers, Kaposi's sarcoma) — switch from CNI to mTOR inhibitor; mTOR inhibition has antineoplastic properties</li>
              </ul>
            </li>
            <li><strong>Side effects:</strong>
              <ul>
                <li><span class="purple-term">Poor wound healing and lymphocele formation</span> — everolimus/sirolimus inhibit fibroblast proliferation; avoid in the first few weeks post-transplant when anastomoses are healing</li>
                <li><span class="purple-term">Hyperlipidaemia</span> (hypertriglyceridaemia, hypercholesterolaemia) — often requires statin therapy</li>
                <li>Mouth ulcers (stomatitis) — dose-dependent</li>
                <li>Pneumonitis — interstitial lung disease (rare but serious)</li>
                <li>Impaired glucose tolerance; myelosuppression</li>
                <li>Peripheral oedema; proteinuria</li>
              </ul>
            </li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Corticosteroids (prednisolone/methylprednisolone)</strong></p>
          <ul>
            <li>Used in three contexts in transplantation:
              <ul>
                <li>Maintenance (low-dose prednisolone 5 mg/day) — broad anti-inflammatory; some centres withdraw at 3–6 months</li>
                <li>Pulse methylprednisolone (500 mg–1 g IV for 3 days) — first-line treatment for biopsy-proven acute T-cell rejection</li>
                <li>Stress dosing — patients on chronic steroids must receive IV hydrocortisone cover during surgery or severe illness</li>
              </ul>
            </li>
            <li>Long-term steroid side effects in transplant recipients: NODAT, osteoporosis (bone protection with calcium + vitamin D ± bisphosphonate), weight gain, hypertension, cataracts, adrenal suppression</li>
          </ul>
        </div>
      `,
    },
    {
      type: "text",
      title: "Rejection: Recognition, Types & Treatment",
      content: `
        <p>Rejection is the adaptive immune response against donor alloantigens. Early recognition and treatment are critical to graft survival.</p>

        <div class="highlight-box">
          <p><strong>Types of rejection</strong></p>
          <ul>
            <li><span class="purple-term">Hyperacute rejection</span>: occurs within <strong>minutes to hours</strong> of transplant; caused by pre-formed donor-specific antibodies (ABO incompatibility or pre-existing anti-HLA antibodies); complement activation → graft thrombosis → nephrectomy required; prevented by cross-match and ABO compatibility testing; now rare</li>
            <li><span class="purple-term">Acute T-cell mediated rejection (TCMR)</span>: days to months post-transplant; T cells recognise donor alloantigens → cytotoxic T-cell attack on tubular and vascular endothelial cells; biopsy: tubulitis, interstitial infiltrates; treatment: pulse methylprednisolone (500 mg IV × 3 days); most episodes respond</li>
            <li><span class="purple-term">Antibody-mediated rejection (ABMR)</span>: can be acute or chronic; de novo or pre-existing donor-specific antibodies (DSA) activate complement on endothelium → microvascular injury; biopsy: C4d positivity (complement deposition), peritubular capillary inflammation, glomerulitis; harder to treat than TCMR; poor long-term graft survival</li>
            <li><span class="purple-term">Chronic rejection (chronic allograft nephropathy)</span>: gradual progressive loss of function over years; combination of immune-mediated injury + CNI nephrotoxicity + hypertension; interstitial fibrosis + tubular atrophy on biopsy (IFTA); no effective treatment; optimise CNI levels, BP control, and proteinuria</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Treatment of acute rejection</strong></p>
          <ul>
            <li><strong>TCMR</strong>: pulse methylprednisolone 500 mg IV × 3 days; if steroid-resistant → ATG (T-cell depletion)</li>
            <li><strong>ABMR</strong> — combination therapy:
              <ul>
                <li>Plasmapheresis (removes circulating DSA)</li>
                <li>IV immunoglobulin (IVIG — neutralises antibodies, provides anti-idiotypic antibodies)</li>
                <li>Rituximab (anti-CD20 — depletes B cells, reduces antibody production)</li>
                <li>Complement inhibitors (eculizumab — targets C5a, used in severe ABMR)</li>
              </ul>
            </li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Donor-specific antibodies (DSA) and surveillance</strong></p>
          <ul>
            <li>DSA monitoring: measured by <span class="purple-term">Luminex bead assay</span> (identifies anti-HLA antibodies against specific donor HLA antigens)</li>
            <li>Rising DSA without clinical rejection = subclinical ABMR risk → may prompt protocol biopsy and treatment adjustment</li>
            <li>Panel reactive antibody (PRA/cPRA): pre-transplant sensitisation level; high cPRA (&gt;85%) indicates broad anti-HLA sensitisation → harder to find a compatible donor; virtual crossmatch used to predict compatibility</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A renal transplant recipient presents 8 weeks after transplantation with rising creatinine (140 → 230 µmol/L). A graft biopsy shows tubulitis, interstitial lymphocytic infiltrates, and no C4d staining. DSA are negative. Tacrolimus level is 6 ng/mL (therapeutic). What is the diagnosis and treatment?",
      options: [
        "CNI (tacrolimus) nephrotoxicity — reduce tacrolimus dose and recheck biopsy",
        "Acute T-cell mediated rejection — treat with pulse IV methylprednisolone 500 mg × 3 days",
        "Antibody-mediated rejection — treat with plasmapheresis, IVIG, and rituximab",
        "CMV nephritis — check CMV viral load and start ganciclovir",
      ],
      correct: 1,
      explanation:
        "The biopsy shows tubulitis and interstitial infiltrates — the hallmark of acute T-cell mediated rejection (TCMR). The absence of C4d staining and negative DSA argues against antibody-mediated rejection. The tacrolimus level is therapeutic, making CNI toxicity less likely (CNI toxicity also does not typically cause tubulitis). Acute TCMR within the first few months is the most common form of acute rejection. Treatment: pulse IV methylprednisolone 500 mg × 3 consecutive days. Most episodes of TCMR respond to high-dose steroids with creatinine returning to baseline. Steroid-resistant TCMR is then treated with anti-thymocyte globulin.",
    },
  ],
};
